// -- Panel ------------------------------------------------------------------
// A card with a title bar - icon, title, tools - whose minimize and maximize
// buttons are Swaps (a checkbox each: the CSS shows the face). This module
// keeps the panel's state in step with those checkboxes, and makes it a
// border-layout citizen after ExtJS 4: minimized in north / south the whole
// region shrinks to the title bar; in west / east the title bar turns into a
// vertical tab and the region shrinks to its width. Maximized, the panel fills
// its border layout (or a [data-panel-host], else the viewport); Escape
// restores it. The named-state API follows AGENTS.md "State API".

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js -
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals } from '../../shared/state-api.js';

const df$ = defussGlobals();

/** default = title bar + body; minimized = the title bar only (in a border
 *  layout region the region shrinks with it); maximized = fills its host. */
const panelStates = ['default', 'minimized', 'maximized'];

const SIDES = ['north', 'south', 'west', 'east', 'center'];

const resolve = (t) => (typeof t === 'string' ? document.getElementById(t) ?? document.querySelector(t) : t);
const toolInput = (panel, tool) => panel.querySelector(`:scope > .panel-header .panel-${tool} > input[type="checkbox"]`);

/**
 * The border-layout region a panel sits in: the panel itself (a fixed region),
 * the resizer it is the pane of, or the center it fills. Anything deeper is
 * not a region panel.
 */
function regionOf(panel) {
  const parent = panel.parentElement;
  if (!parent) return null;
  if (parent.classList.contains('border-layout')) return panel;
  if (parent.classList.contains('resizer') && parent.parentElement?.classList.contains('border-layout')) return parent;
  if (parent.classList.contains('border-layout-center') && parent.parentElement?.classList.contains('border-layout')) return parent;
  return null;
}
const sideOf = (region) => (region ? SIDES.find((s) => region.classList.contains(`border-layout-${s}`)) ?? null : null);

/** The element a maximized panel fills: its border layout or a [data-panel-host]. */
const hostOf = (panel) => panel.parentElement?.closest('.border-layout, [data-panel-host]') ?? null;

// -- State API ------------------------------------------------------------------

/** The only function that touches the DOM for a state change. */
function triggerStateChange(panel, stateName) {
  const minimized = stateName === 'minimized';
  const maximized = stateName === 'maximized';
  panel.toggleAttribute('data-minimized', minimized);
  panel.toggleAttribute('data-maximized', maximized);

  // the swaps show the state - a checkbox each
  const min = toolInput(panel, 'minimize');
  const max = toolInput(panel, 'maximize');
  if (min) min.checked = minimized;
  if (max) max.checked = maximized;

  // the body leaves the a11y tree with the card
  const body = panel.querySelector(':scope > .panel-body');
  if (body) body.toggleAttribute('inert', minimized);

  // a region shrinks to the title bar; its divider rests until it comes back
  const region = regionOf(panel);
  if (region && region !== panel) {
    region.toggleAttribute('data-panel-minimized', minimized);
    const handle = region.querySelector(':scope > .resizer-handle');
    if (handle) handle.inert = minimized || maximized;
  }

  // maximized: the host becomes the positioning context
  const host = hostOf(panel);
  if (maximized && host) {
    panel._host = host;
    host.setAttribute('data-panel-maximized', '');
  } else if (panel._host) {
    if (!panel._host.querySelector('.panel[data-maximized]')) panel._host.removeAttribute('data-panel-maximized');
    panel._host = null;
  }
}

export const panelApi = {
  setState(panel, stateName, config = {}) {
    if (!panelStates.includes(stateName)) {
      throw new Error(`panel: unknown state "${stateName}" (supported: ${panelStates.join(', ')})`);
    }
    const from = panel.dataset.stateName || 'default';
    triggerStateChange(panel, stateName);
    panel.dataset.stateName = stateName;
    panel._stateConfig = config;
    if (from !== stateName) {
      panel.dispatchEvent(new CustomEvent('panel-change', { bubbles: true, detail: { state: stateName, previous: from, region: sideOf(regionOf(panel)) } }));
    }
  },
  getState(panel) {
    return { name: panel.dataset.stateName || 'default', config: panel._stateConfig ?? {} };
  },
};

df$.panelApi = panelApi;
df$.panelStates = panelStates;

// -- init --------------------------------------------------------------------------

function init() {
  document.querySelectorAll('.panel:not([data-init])').forEach((panel) => {
    panel.dataset.init = '';

    // where it sits: data-region drives the vertical title bar and the
    // direction the minimize chevron points
    const side = sideOf(regionOf(panel));
    if (side) panel.dataset.region = side;

    const header = panel.querySelector(':scope > .panel-header');
    const body = panel.querySelector(':scope > .panel-body');
    if (body) {
      if (!body.id) body.id = `panel-${Math.random().toString(36).slice(2, 8)}-body`;
      toolInput(panel, 'minimize')?.setAttribute('aria-controls', body.id);
    }

    // the swaps: a checked minimize is 'minimized', a checked maximize 'maximized'
    panel.addEventListener('change', (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.closest('.panel') !== panel) return;
      if (input === toolInput(panel, 'minimize')) panelApi.setState(panel, input.checked ? 'minimized' : 'default');
      else if (input === toolInput(panel, 'maximize')) panelApi.setState(panel, input.checked ? 'maximized' : 'default');
    });

    // a double-click on the title bar (not on a tool) minimizes / restores -
    // data-title-collapse="false" switches it off
    header?.addEventListener('dblclick', (e) => {
      if (panel.dataset.titleCollapse === 'false' || e.target.closest('.panel-tools') || !toolInput(panel, 'minimize')) return;
      document.getSelection()?.removeAllRanges();
      panelApi.setState(panel, panel.hasAttribute('data-minimized') ? 'default' : 'minimized');
    });

    // Escape leaves maximized
    panel.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !panel.hasAttribute('data-maximized')) return;
      e.stopPropagation();
      panelApi.setState(panel, 'default');
      toolInput(panel, 'maximize')?.focus();
    });

    panel.api = {
      setState: (stateName, config) => panelApi.setState(panel, stateName, config),
      getState: () => panelApi.getState(panel),
    };

    // markup may start minimized / maximized (attribute or a checked swap)
    const start = panel.hasAttribute('data-maximized') || toolInput(panel, 'maximize')?.checked ? 'maximized'
      : panel.hasAttribute('data-minimized') || toolInput(panel, 'minimize')?.checked ? 'minimized'
        : 'default';
    triggerStateChange(panel, start);
    panel.dataset.stateName = start;
    panel._stateConfig = {};
  });
}

// -- df$.shadcn.panel: the imperative surface ------------------------------------------

const act = (t, state) => {
  const panel = resolve(t);
  if (panel?.api) panel.api.setState(state);
  return panel ?? null;
};

df$.panel = {
  /** Title bar only - in a border layout region, the region shrinks with it. */
  minimize: (t) => act(t, 'minimized'),
  /** Fills its border layout / [data-panel-host] / the viewport. */
  maximize: (t) => act(t, 'maximized'),
  /** Back to title bar + body at the authored size. */
  restore: (t) => act(t, 'default'),
  /** Minimizes or restores; returns whether it is now minimized. */
  toggle: (t) => {
    const panel = resolve(t);
    if (!panel?.api) return false;
    panel.api.setState(panel.hasAttribute('data-minimized') ? 'default' : 'minimized');
    return panel.hasAttribute('data-minimized');
  },
};

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });
